/**
 * Remark plugin that turns inline `<cite id="<id>"/>` markers in streamed
 * assistant text into citation-pill nodes at markdown render time.
 *
 * The chat backend (`stream-events.ts`) splices `<cite id="<id>"/>` markers into
 * the streamed text at the exact position each citation arrives — including
 * mid-paragraph, which is what option (1) was chosen for. We can't render
 * those markers as raw text (they'd leak as visible noise), and we can't
 * post-process the React output (offset bookkeeping after markdown→React is
 * brittle). So we do the splice during the markdown→hast pass, where we
 * still have clean text-node boundaries.
 *
 * Implementation: for each `text` node in the mdast tree, run
 * `splitTextOnCitationMarkers` (the pure logic, kept in `citation-markers.ts`
 * so it stays unit-testable without ESM-only deps) and replace the node with
 * the resulting mix of text + link nodes (URL = `cite:<id>`). The chat's
 * `MarkdownText` registers a `components.a` override that detects the `cite:`
 * scheme via `getCitationIdFromHref` and renders a `CitationPill` (looked up
 * by id from the module-level citation registry) instead of an anchor.
 *
 * Why a `link` node — rather than a custom node type with `data.hName`:
 * mdast-util-to-hast (which `react-markdown` uses) ignores `data.hName` on
 * `text` nodes, and adding a brand-new mdast type requires a custom
 * `remark-rehype` handler that's awkward to thread through assistant-ui's
 * `makeMarkdownText`. A `link` node round-trips through the standard pipeline
 * cleanly and is intercepted by the existing components mechanism.
 *
 * Edge cases:
 *   - Markers that span across markdown elements (e.g. half inside `**bold**`)
 *     don't occur in practice — the backend emits the marker as a single,
 *     unbroken text-delta. No split-marker handling is attempted.
 *   - User-typed `<cite id="foo"/>` in their input would also match. Acceptable:
 *     the lookup against the citation registry would miss and the pill would
 *     render as nothing (the renderer fails closed). User text is the
 *     adversary's input here — not a security boundary, just visual noise.
 *
 * PLT-4896: also visit `html` mdast nodes. micromark/`remarkParse`
 * occasionally tokenises a stand-alone `<cite id="…"/>` as an `html`
 * node rather than as part of the surrounding `text` node — typically
 * when the directive sits at the start of a soft-wrapped line, after a
 * `**bold**` span, or with whitespace inside the tag. The `text`-only
 * visitor never sees it, and we don't enable `rehype-raw` (raw HTML is
 * otherwise stripped by `react-markdown`), so without this fallthrough
 * the raw `<cite … />` characters bleed straight into the rendered
 * DOM. Mirrors the `inlineCode` fallthrough added to `remark-lock-
 * badges.ts` in PLT-4661 (and that plugin's own `html` pass in this
 * change). Malformed-cite stripping (PLT-4806) flows through unchanged
 * because the same splitter does the work.
 */
import { SKIP, visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Html, Root, Text } from 'mdast';
import { splitTextOnCitationMarkers, type MarkerSplitChild } from './citation-markers';

export const remarkCitationPills: Plugin<[], Root> = () => {
  return tree => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnCitationMarkers(node.value);
      if (newChildren === null) return;
      // `parent.children` is typed as a union that doesn't include `link` for
      // every parent type (e.g. table cells). The mdast spec does allow `link`
      // wherever a `text` node is allowed, so the cast is safe.
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      // Skip the newly-inserted nodes — we've already finished with them, and
      // the inner `text` nodes we created don't contain markers.
      return [SKIP, index + newChildren.length];
    });
    // Second pass (PLT-4896): tolerate html-tokenised markers. A bare
    // `<cite id="…"/>` directive that micromark tokenises as an `html`
    // mdast node never reaches the `text` visitor; without this pass
    // it would render as raw source characters because we don't run
    // `rehype-raw`. Replace the html node with the same text/link
    // sequence the `text` visitor would produce. Malformed `<cite>`
    // tags continue to drop to nothing (or to their inner text for
    // paired forms) — that contract lives in the splitter, so it
    // applies to all three visitors uniformly.
    visit(tree, 'html', (node: Html, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnCitationMarkers(node.value);
      if (newChildren === null) return;
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
  };
};
